import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate, Navigate, Link } from 'react-router-dom'
import { getApiBase, getToken } from '../lib/utils'
import WhatsAppStatsWidget from '../components/WhatsAppStatsWidget'
import { useWebSocket } from '../hooks/useWebSocket'
import toast, { Toaster } from 'react-hot-toast'
import { useAppLanguage } from '../i18n'

// Helper pour formater date relative avec locale
function formatRelativeTime(dateStr: string, t: any): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  
  if (diffMins < 1) return t.conversations.time.justNow
  if (diffMins < 60) return t.conversations.time.minutesAgo.replace('{n}', String(diffMins))
  if (diffHours < 24) return t.conversations.time.hoursAgo.replace('{n}', String(diffHours))
  if (diffDays < 7) return t.conversations.time.daysAgo.replace('{n}', String(diffDays))
  return date.toLocaleDateString('fr-FR')
}

// Helper pour obtenir le badge de source
type SourceType = 'whatsapp' | 'widget_external' | 'widget_internal';
function getSourceBadge(source: SourceType, t: any) {
  switch (source) {
    case 'whatsapp':
      return {
        label: t.conversations.sources.whatsapp,
        icon: '💬',
        classes: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      }
    case 'widget_external':
      return {
        label: t.conversations.sources.widgetExternal,
        icon: '🔌',
        classes: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
      }
    case 'widget_internal':
    default:
      return {
        label: t.conversations.sources.widgetInternal,
        icon: '🌐',
        classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
      }
  }
}

interface Conversation {
  id: number
  user_id: number
  agent_id: number
  agent_name: string
  title: string
  is_favorite: boolean
  source: 'widget_internal' | 'widget_external' | 'whatsapp'
  message_count: number
  human_handoff: boolean
  created_at: string
  updated_at: string
}

interface Agent {
  id: number
  name: string
}

interface User {
  id: number
  email: string
  first_name?: string
  last_name?: string
}

interface Message {
  id: number
  conversation_id: number
  role: string
  content: string
  tool_calls: any
  tool_call_id: string | null
  tool_name: string | null
  created_at: string
}

export default function ConversationsPage() {
  const { t, language } = useAppLanguage()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const lockedAgentId = useMemo(() => {
    const raw = searchParams.get('agent_id')
    if (raw == null || raw.trim() === '') return null
    const n = Number(raw)
    if (!Number.isFinite(n) || n < 1) return null
    return n
  }, [searchParams])
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [mobileView, setMobileView] = useState<'list' | 'detail'>('list')
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // États filtres (initialisés depuis URL)
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedSource, setSelectedSource] = useState<'all' | 'whatsapp' | 'widget_external' | 'widget_internal'>(
    (searchParams.get('source') as 'all' | 'whatsapp' | 'widget_external' | 'widget_internal') || 'all'
  )
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [sortBy, setSortBy] = useState('updated_at')
  const [sortOrder, setSortOrder] = useState('desc')
  const [messageFilter, setMessageFilter] = useState<'all' | 'short' | 'medium' | 'long'>('all')
  const [periodFilter, setPeriodFilter] = useState<'all' | 'today' | 'week' | 'month'>('all')
  
  // Listes pour dropdowns
  const [agents, setAgents] = useState<Agent[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState<'all' | 'favorites'>('all')
  const [allAgents, setAllAgents] = useState<Agent[]>([])
  const [showFilters, setShowFilters] = useState(false)
  
  // Phase 1: WebSocket temps réel
  const { isConnected, subscribe } = useWebSocket()

  // Calculer le prénom
  const firstName = useMemo(() => {
    if (currentUser?.first_name) return currentUser.first_name
    if (!currentUser?.email) return 'Profil'
    const local = String(currentUser.email).split('@')[0]
    const parts = local.replace(/\d+/g, '').split(/[._-]+/).filter(Boolean)
    const name = (parts[0] || local)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }, [currentUser])

  // Charger user actuel
  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null')
      setCurrentUser(user)
    } catch {}
  }, [])

  // Charger agents et users une seule fois
  useEffect(() => {
    loadAgents()
    if (currentUser?.is_superuser) {
      console.log('Loading users (admin mode)')
      loadUsers()
    } else {
      console.log('Not admin, skipping users load', currentUser)
    }
  }, [currentUser])

  // Charger conversations quand filtres changent
  useEffect(() => {
    if (!currentUser || lockedAgentId == null) return
    loadConversations()
  }, [searchTerm, lockedAgentId, selectedUserId, selectedSource, favoritesOnly, sortBy, sortOrder, messageFilter, periodFilter, currentUser])
  
  // Phase 1: Écouter événements WebSocket temps réel
  // Fetch UNE seule conversation (optimisé)
  const fetchSingleConversation = async (conversationId: number) => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations?limit=100&sort_by=updated_at&sort_order=desc`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) return
      
      const data = await response.json()
      const updatedConv = data.items.find((c: Conversation) => c.id === conversationId)
      
      if (updatedConv) {
        updateConversationIncremental(conversationId, updatedConv)
      }
    } catch (err) {
      console.error('Erreur fetch conversation:', err)
    }
  }

  // Mise à jour incrémentale d'une conversation spécifique (sans recharger toute la liste)
  const updateConversationIncremental = (conversationId: number, updates: Partial<Conversation>) => {
    setConversations(prevConvs => {
      const existingIndex = prevConvs.findIndex(c => c.id === conversationId)
      
      if (existingIndex >= 0) {
        // Mettre à jour la conversation existante avec les nouvelles données
        const newConvs = [...prevConvs]
        newConvs[existingIndex] = {
          ...newConvs[existingIndex],
          ...updates
        }
        
        // Trier par updated_at si nécessaire (pour remonter en haut)
        if (sortBy === 'updated_at') {
          return newConvs.sort((a, b) => {
            const dateA = new Date(a.updated_at).getTime()
            const dateB = new Date(b.updated_at).getTime()
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
          })
        }
        
        return newConvs
      }
      
      return prevConvs
    })
  }

  useEffect(() => {
    if (!subscribe) return
    
    // Écouter conversation_update (nouveau message, changement statut)
    const unsubscribeConv = subscribe('conversation_update', (data) => {
      console.log('🔄 Rafraîchissement auto conversation:', data.conversation_id)
      console.log('📊 Données événement:', JSON.stringify(data, null, 2))
      
      // Toast notification EN PREMIER
      if (data.action === 'message_added') {
        toast(`💬 ${t.conversations.toast.newMessage}`, {
          duration: 3000,
          icon: '📨',
        })
      }
      
      // Mise à jour incrémentale de la conversation (sans recharger toute la page)
      // Utiliser les données de l'événement WebSocket directement (optimisé)
      if (data.data?.message_count !== undefined && data.data?.updated_at) {
        console.log('✅ Mise à jour incrémentale:', {
          conversation_id: data.conversation_id,
          message_count: data.data.message_count,
          updated_at: data.data.updated_at
        })
        updateConversationIncremental(data.conversation_id, {
          message_count: data.data.message_count,
          updated_at: data.data.updated_at
        })
      } else {
        console.warn('⚠️ Données manquantes, fallback sur fetch API')
        // Fallback : faire un fetch pour cette conversation spécifique
        fetchSingleConversation(data.conversation_id)
      }
      
      // Si la conversation est sélectionnée, rafraîchir les messages
      if (selectedConversation && selectedConversation.id === data.conversation_id) {
        loadMessages(data.conversation_id)
      }
    })
    
    // Écouter takeover_event (takeover/release manuel ou auto)
    const unsubscribeTakeover = subscribe('takeover_event', (data) => {
      console.log('👤 Événement takeover:', data.event_type)
      
      // Rafraîchir la liste
      loadConversations()
      
      // Toast selon le type
      if (data.event_type === 'takeover') {
        toast.success(`👤 ${t.conversations.toast.takeoverActivated}`, {
          duration: 3000,
        })
      } else if (data.event_type === 'release') {
        toast.success(`🤖 ${t.conversations.toast.agentReactivated}`, {
          duration: 3000,
        })
      } else if (data.event_type === 'auto_takeover') {
        toast(`🤖→👤 ${t.conversations.toast.autoTakeover}`, {
          duration: 4000,
          icon: '🔔',
        })
      }
    })
    
    // Écouter notification (système)
    const unsubscribeNotif = subscribe('notification', (data) => {
      const icon = data.type === 'error' ? '❌' : data.type === 'warning' ? '⚠️' : data.type === 'success' ? '✅' : 'ℹ️'
      
      if (data.type === 'error') {
        toast.error(data.message, { duration: 5000, icon })
      } else if (data.type === 'warning') {
        toast(data.message, { duration: 4000, icon })
      } else if (data.type === 'success') {
        toast.success(data.message, { duration: 3000, icon })
      } else {
        toast(data.message, { duration: 3000, icon })
      }
    })
    
    // Cleanup
    return () => {
      unsubscribeConv()
      unsubscribeTakeover()
      unsubscribeNotif()
    }
  }, [subscribe, selectedConversation])

  const loadConversations = async () => {
    try {
      setLoading(true)
      const token = getToken()
      
      // Construire query params
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (lockedAgentId != null) params.append('agent_id', String(lockedAgentId))
      if (selectedUserId && currentUser?.is_superuser) params.append('user_id', String(selectedUserId))
      if (selectedSource !== 'all') params.append('source', selectedSource)
      if (favoritesOnly) params.append('favorites_only', 'true')
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)
      
      // Filtre période
      if (periodFilter !== 'all') {
        const now = new Date()
        let dateFrom = new Date()
        if (periodFilter === 'today') {
          dateFrom.setHours(0, 0, 0, 0)
        } else if (periodFilter === 'week') {
          dateFrom.setDate(now.getDate() - 7)
        } else if (periodFilter === 'month') {
          dateFrom.setMonth(now.getMonth() - 1)
        }
        params.append('date_from', dateFrom.toISOString())
      }
      
      // Filtre messages
      if (messageFilter === 'short') {
        params.append('max_messages', '3')
      } else if (messageFilter === 'medium') {
        params.append('min_messages', '4')
        params.append('max_messages', '10')
      } else if (messageFilter === 'long') {
        params.append('min_messages', '11')
      }
      
      const response = await fetch(`${getApiBase()}/conversations?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(t.conversations.errorLoading)
      }

      const data = await response.json()
      console.log('Conversations loaded (full):', JSON.stringify(data.items?.slice(0, 1), null, 2))
      setConversations(data.items || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }
  
  const loadAgents = async () => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setAgents(data.items || [])
      }
    } catch {}
  }
  
  const loadUsers = async () => {
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/auth/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        console.log('Users loaded:', data)
        setUsers(Array.isArray(data) ? data : (data.items || []))
      } else {
        console.error('Failed to load users:', response.status)
      }
    } catch (err) {
      console.error('Error loading users:', err)
    }
  }

  // Charger messages d'une conversation
  const loadMessages = async (conversationId: number) => {
    try {
      setMessagesLoading(true)
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(t.conversations.errorLoading)
      }

      const data = await response.json()
      setMessages(data.messages || [])
    } catch (err: any) {
      setError(err.message)
    } finally {
      setMessagesLoading(false)
    }
  }

  // Sélectionner conversation
  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation(conv)
    setMobileView('detail')
    loadMessages(conv.id)
  }

  // Supprimer conversation
  const handleDeleteConversation = async (conversationId: number) => {
    if (!confirm(t.conversations.delete.confirm)) {
      return
    }

    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(t.conversations.delete.error)
      }

      // Recharger liste
      loadConversations()
      
      // Désélectionner si c'était la conversation active
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null)
        setMessages([])
      }
    } catch (err: any) {
      alert(`${t.conversations.delete.error}: ${err.message}`)
    }
  }

  // Toggle favori
  const handleToggleFavorite = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/conversations/${conversationId}/favorite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Mettre à jour localement
        setConversations(conversations.map(conv =>
          conv.id === conversationId
            ? { ...conv, is_favorite: !conv.is_favorite }
            : conv
        ))
        
        // Mettre à jour la conversation sélectionnée si c'est celle-ci
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation({
            ...selectedConversation,
            is_favorite: !selectedConversation.is_favorite
          })
        }
      }
    } catch (err) {
      console.error('Error toggle favorite:', err)
    }
  }

  // Ouvrir l'agent de cette conversation dans le Messenger
  const handleOpenInMessenger = (agentId: number) => {
    navigate(`/messenger/${agentId}`)
  }

  // Prendre le contrôle d'une conversation (Human Takeover)
  const handleTakeover = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/conversations/${conversationId}/takeover`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Recharger pour mettre à jour le statut
        await loadConversations()
        // Si c'est la conversation sélectionnée, recharger aussi
        if (selectedConversation?.id === conversationId) {
          const updated = conversations.find(c => c.id === conversationId)
          if (updated) setSelectedConversation(updated)
        }
      } else {
        const data = await response.json()
        alert(`${t.conversations.delete.error}: ${data.detail || t.conversations.humanTakeover.takeover}`)
      }
    } catch (err) {
      console.error('Error takeover:', err)
      alert(t.conversations.delete.error)
    }
  }

  // Relâcher le contrôle d'une conversation
  const handleRelease = async (conversationId: number, event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      const token = getToken()
      const response = await fetch(`${getApiBase()}/whatsapp/conversations/${conversationId}/release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        // Recharger pour mettre à jour le statut
        await loadConversations()
        // Si c'est la conversation sélectionnée, recharger aussi
        if (selectedConversation?.id === conversationId) {
          const updated = conversations.find(c => c.id === conversationId)
          if (updated) setSelectedConversation(updated)
        }
      } else {
        const data = await response.json()
        alert(`${t.conversations.delete.error}: ${data.detail || t.conversations.humanTakeover.release}`)
      }
    } catch (err) {
      console.error('Error release:', err)
      alert(t.conversations.delete.error)
    }
  }

  if (lockedAgentId == null) {
    return <Navigate to="/agents" replace />
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">{t.conversations.loading}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded p-4 text-red-700">
          {t.conversations.errorLoading}: {error}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <div className="flex flex-1 overflow-hidden rounded-none border border-emerald-100/70 bg-white/90 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
        {/* Liste conversations (sidebar) */}
        <div className={`${mobileView === 'detail' ? 'hidden' : 'flex flex-col'} lg:flex lg:flex-col w-full lg:w-1/3 bg-white/50 dark:bg-zinc-900/50 border-r border-emerald-100/70 dark:border-emerald-900/30 overflow-y-auto`}>
        <div className="p-3 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/20 space-y-2">
          <div className="flex items-center justify-between gap-2 text-[11px] text-zinc-500 dark:text-zinc-400">
            <span className="truncate min-w-0">
              {t.conversations.historyForAgent.replace(
                '{name}',
                agents.find((a) => a.id === lockedAgentId)?.name ?? `#${lockedAgentId}`
              )}
            </span>
            <Link
              to="/agents"
              className="shrink-0 font-medium text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              {t.conversations.backToAgents}
            </Link>
          </div>
          {/* Ligne 1 : search + bouton filtre */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t.conversations.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-7 pr-3 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-emerald-500/60"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition ${
                showFilters || selectedSource !== 'all' || favoritesOnly || periodFilter !== 'all' || messageFilter !== 'all'
                  ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-400'
                  : 'border-zinc-700/50 bg-zinc-800/60 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
              </svg>
              {language === 'fr' ? 'Filtres' : language === 'es' ? 'Filtros' : 'Filters'}
              {(selectedSource !== 'all' || favoritesOnly || periodFilter !== 'all' || messageFilter !== 'all') && (
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              )}
            </button>
          </div>

          {/* Ligne 2 : select source compact */}
          <select
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value as any)}
            className="w-full px-2 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 focus:outline-none focus:border-emerald-500/60"
          >
            <option value="all">{t.conversations.allSources}</option>
            <option value="whatsapp">💬 {t.conversations.whatsApp}</option>
            <option value="widget_external">🔌 {t.conversations.widgetExt}</option>
            <option value="widget_internal">🌐 {t.conversations.widgetInt}</option>
          </select>

          {/* Badge reset + compteur */}
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-zinc-500">{conversations.length} {t.conversations.conversationsCount}</span>
            <div className="flex items-center gap-2">
              {isConnected && (
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {t.conversations.realtime}
                </span>
              )}
              {(favoritesOnly || periodFilter !== 'all' || messageFilter !== 'all') && (
                <button
                  onClick={() => { setFavoritesOnly(false); setPeriodFilter('all'); setMessageFilter('all') }}
                  className="flex items-center gap-1 text-[10px] text-emerald-400 hover:text-emerald-300 transition"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  {t.conversations.resetFilters}
                </button>
              )}
            </div>
          </div>

          {/* WhatsApp Stats */}
          {selectedSource === 'whatsapp' && (
            <WhatsAppStatsWidget />
          )}
        </div>

        {/* Panneau filtres avancés */}
        {showFilters && (
          <div className="p-3 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-zinc-900/60 space-y-2">
            <div className="px-2 py-1.5 text-xs rounded-lg border border-zinc-700/40 bg-zinc-800/40 text-zinc-400">
              {t.conversations.agent}:{' '}
              <span className="text-zinc-200">
                {agents.find((a) => a.id === lockedAgentId)?.name ?? `#${lockedAgentId}`}
              </span>
            </div>

            {currentUser?.is_superuser && (
              <select
                value={selectedUserId || ''}
                onChange={(e) => setSelectedUserId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-2 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 focus:outline-none"
              >
                <option value="">{t.conversations.allUsers}</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.email} {user.first_name && `(${user.first_name})`}
                  </option>
                ))}
              </select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value as any)}
                className="px-2 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 focus:outline-none"
              >
                <option value="all">{t.conversations.allPeriods}</option>
                <option value="today">{t.conversations.today}</option>
                <option value="week">{t.conversations.thisWeek}</option>
                <option value="month">{t.conversations.thisMonth}</option>
              </select>
              <select
                value={messageFilter}
                onChange={(e) => setMessageFilter(e.target.value as any)}
                className="px-2 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 focus:outline-none"
              >
                <option value="all">{t.conversations.allMessages}</option>
                <option value="short">{t.conversations.short}</option>
                <option value="medium">{t.conversations.medium}</option>
                <option value="long">{t.conversations.long}</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 focus:outline-none"
              >
                <option value="updated_at">{t.conversations.sortBy.updatedAt}</option>
                <option value="created_at">{t.conversations.sortBy.createdAt}</option>
                <option value="title">{t.conversations.sortBy.title}</option>
              </select>
              <button
                onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                className="px-2.5 py-1.5 text-xs rounded-lg border border-zinc-700/50 bg-zinc-800/60 text-zinc-300 hover:bg-zinc-700 transition"
              >
                {sortOrder === 'desc' ? '↓' : '↑'}
              </button>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-zinc-400">
                <input type="checkbox" checked={favoritesOnly} onChange={(e) => setFavoritesOnly(e.target.checked)} className="w-3.5 h-3.5" />
                ⭐
              </label>
            </div>
            <button
              onClick={() => { setSearchTerm(''); setSelectedUserId(null); setFavoritesOnly(false); setSortBy('updated_at'); setSortOrder('desc'); setMessageFilter('all'); setPeriodFilter('all') }}
              className="w-full py-1.5 text-xs text-zinc-500 hover:text-zinc-300 border border-zinc-700/40 rounded-lg hover:border-zinc-600 transition"
            >
              {t.conversations.resetFilters}
            </button>
          </div>
        )}

        <div className="divide-y divide-emerald-100/50 dark:divide-emerald-900/20">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t.conversations.noConversations}
            </div>
          ) : (
            conversations.map((conv) => {
              // Debug: vérifier le type de is_favorite
              if (conv.id === 1) {
                console.log('Conv 1 - is_favorite:', conv.is_favorite, 'type:', typeof conv.is_favorite)
              }
              return (
              <div
                key={conv.id}
                className={`p-4 cursor-pointer hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30 transition ${
                  selectedConversation?.id === conv.id ? 'bg-emerald-100/60 dark:bg-emerald-900/30 border-l-4 border-emerald-500' : ''
                }`}
                onClick={() => handleSelectConversation(conv)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900 dark:text-white truncate">
                        {conv.title}
                      </h3>
                      {conv.is_favorite && <span className="text-yellow-500">⭐</span>}
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t.conversations.agent}: {conv.agent_name}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                      {/* Badge source */}
                      {(() => {
                        const badge = getSourceBadge(conv.source, t)
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.classes}`}>
                            <span>{badge.icon}</span>
                            <span>{badge.label}</span>
                          </span>
                        )
                      })()}
                      {/* Badge Human Takeover */}
                      {conv.source === 'whatsapp' && (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          conv.human_handoff
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        }`}>
                          <span>{conv.human_handoff ? t.conversations.humanTakeover.human : t.conversations.humanTakeover.auto}</span>
                        </span>
                      )}
                      <span className="text-gray-400 dark:text-gray-500">{conv.message_count} {t.conversations.messages}</span>
                      <span className="text-gray-400 dark:text-gray-500">•</span>
                      <span className="text-gray-400 dark:text-gray-500">
                        {formatRelativeTime(conv.updated_at, t)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {/* Boutons Takeover/Release (WhatsApp uniquement) */}
                    {conv.source === 'whatsapp' && (
                      conv.human_handoff ? (
                        <button
                          onClick={(e) => handleRelease(conv.id, e)}
                          className="p-1.5 text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 transition"
                          title={t.conversations.humanTakeover.releaseTitle}
                        >
                          <span className="text-lg">🤖</span>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => handleTakeover(conv.id, e)}
                          className="p-1.5 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 transition"
                          title={t.conversations.humanTakeover.takeoverTitle}
                        >
                          <span className="text-lg">👤</span>
                        </button>
                      )
                    )}
                    <button
                      onClick={(e) => handleToggleFavorite(conv.id, e)}
                      className={`p-1 transition ${conv.is_favorite ? 'text-yellow-500 hover:text-yellow-600' : 'text-gray-400 hover:text-yellow-500'}`}
                      title={conv.is_favorite ? t.conversations.favorite.remove : t.conversations.favorite.add}
                    >
                      <span className="text-xl">{conv.is_favorite ? '⭐' : '☆'}</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteConversation(conv.id)
                      }}
                      className="p-1 text-gray-400 hover:text-red-600 transition"
                      title={t.common.delete}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              )
            })
          )}
        </div>
      </div>

      {/* Messages (main) */}
      <div className={`${mobileView === 'list' ? 'hidden' : 'flex flex-col'} lg:flex lg:flex-col flex-1`}>
        {selectedConversation ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-emerald-100/70 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/20 flex items-center gap-3">
              {/* Bouton retour mobile */}
              <button
                onClick={() => setMobileView('list')}
                className="lg:hidden flex-shrink-0 p-2 rounded-full text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800 transition"
                aria-label="Retour à la liste"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white truncate">{selectedConversation.title}</h2>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
                  {t.conversations.agent}: {selectedConversation.agent_name} • {selectedConversation.message_count} {t.conversations.messages}
                </p>
              </div>
              <button
                onClick={() => handleOpenInMessenger(selectedConversation.agent_id)}
                className="flex-shrink-0 px-3 sm:px-4 py-2 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-600 transition flex items-center gap-2 shadow-lg shadow-emerald-500/30 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                Ouvrir dans Messenger
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-b from-white/50 to-emerald-50/30 dark:from-zinc-900/50 dark:to-zinc-950/50">
              {messagesLoading ? (
                <div className="text-center text-gray-500 dark:text-gray-400">{t.common.loading}</div>
              ) : messages.length === 0 ? (
                <div className="text-center text-gray-500 dark:text-gray-400">{t.conversations.noConversations}</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-2xl rounded-2xl p-4 ${
                        msg.role === 'user'
                          ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
                          : msg.role === 'assistant'
                          ? 'bg-white/90 dark:bg-zinc-800/90 border border-emerald-100/70 dark:border-emerald-900/30 shadow-md'
                          : 'bg-gray-100 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700'
                      }`}
                    >
                      {/* Role badge */}
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                            msg.role === 'user'
                              ? 'bg-emerald-400 text-white'
                              : msg.role === 'assistant'
                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                              : 'bg-gray-200 text-gray-700'
                          }`}
                        >
                          {msg.role === 'user' ? (language === 'fr' ? 'Vous' : language === 'es' ? 'Tú' : 'You') : msg.role === 'assistant' ? 'Assistant' : msg.role}
                        </span>
                        <span className={`text-xs ${msg.role === 'user' ? 'text-emerald-100' : 'text-gray-400'}`}>
                          {formatRelativeTime(msg.created_at, t)}
                        </span>
                      </div>

                      {/* Content */}
                      <div className={`whitespace-pre-wrap ${msg.role === 'user' ? 'text-white' : 'text-gray-900 dark:text-gray-100'}`}>
                        {msg.content || <em className="text-gray-400 dark:text-gray-500">{t.conversations.noConversations}</em>}
                      </div>

                      {/* Tool calls */}
                      {msg.tool_calls && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-zinc-700">
                          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Tools called:</div>
                          <pre className="text-xs bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-gray-100 p-2 rounded overflow-x-auto">
                            {JSON.stringify(msg.tool_calls, null, 2)}
                          </pre>
                        </div>
                      )}

                      {/* Tool result */}
                      {msg.tool_name && (
                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          🔧 Tool result: {msg.tool_name}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-white/50 to-emerald-50/30 dark:from-zinc-900/50 dark:to-zinc-950/50">
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                <svg className="w-12 h-12 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">{t.conversations.noConversations}</p>
              <p className="text-sm mt-2 text-gray-500 dark:text-gray-400">{t.conversations.noConversations}</p>
            </div>
          </div>
        )}
      </div>
        </div>
      </div>
      
      {/* Phase 1: Toast notifications temps réel */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: 'var(--toast-bg)',
            color: 'var(--toast-text)',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  )
}
